// frontend/admin-portal/src/pages/Profile.jsx
import React, { useEffect, useState } from "react";
import {
  PageHeader,
  Card,
  Button,
  Input,
  Alert,
  Spinner,
  FormSection,
  PasswordField,
  AvatarUploader,
} from "@fiberhome/shared-ui";
import { useSettings } from "../contexts/SettingsContext";   // ← moved here
import { profileService } from "../services/profileService";
import { fileService, resolveImageUrl } from "../services/fileService";
import { useAuth } from "../contexts/AuthContext";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { get } = useSettings();

  // ── Load profile
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ── Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveOk, setSaveOk] = useState(false);

  // ── Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwOk, setPwOk] = useState(false);

  // ── Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const loadProfile = async () => {
    setLoading(true);
    setError("");
    try {
      const p = await profileService.getMe();
      setProfile(p);
      setFullName(p.fullName || "");
      setEmail(p.email || "");
      setPhone(p.phone || "");
      setAvatar(p.avatar || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      setSessions(await profileService.listSessions());
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    loadSessions();
  }, []);

  // ── Save profile
  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveError("");
    setSaveOk(false);
    try {
      const updated = await profileService.updateMe({
        fullName,
        email,
        phone,
        avatar,
      });
      setProfile(updated);
      if (setUser) setUser(updated);
      setSaveOk(true);
    } catch (err) {
      setSaveError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Avatar upload
  const handleAvatarUpload = async (file) => {
    const uploaded = await fileService.uploadImage(file, {
      entityType: "users",
    });
    return uploaded?.url || uploaded?.file?.url || uploaded;
  };

  // ── Change password
  const handleChangePassword = async () => {
    setPwError("");
    setPwOk(false);
    if (newPassword !== confirmPassword) {
      setPwError("Passwords don't match");
      return;
    }
    if (newPassword.length < 8) {
      setPwError("Password must be at least 8 characters");
      return;
    }
    setPwSaving(true);
    try {
      await profileService.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPwOk(true);
      // Refresh sessions — password change invalidates old refresh tokens on most backends
      loadSessions();
    } catch (err) {
      setPwError(err.response?.data?.message || err.message || "Change failed");
    } finally {
      setPwSaving(false);
    }
  };

  // ── Session revoke
  const handleRevokeSession = async (id) => {
    if (!window.confirm("Revoke this session? The device will be logged out."))
      return;
    try {
      await profileService.revokeSession(id);
      loadSessions();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const handleRevokeOthers = async () => {
    if (
      !window.confirm(
        "Revoke all other sessions? You'll stay logged in on this device.",
      )
    )
      return;
    try {
      await profileService.revokeOtherSessions();
      loadSessions();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Profile" breadcrumbs={["Home", "Profile"]} />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !profile) {
    return (
      <>
        <PageHeader title="Profile" breadcrumbs={["Home", "Profile"]} />
        <Alert variant="danger">{error || "Could not load profile"}</Alert>
      </>
    );
  }

  const initials = (profile.fullName || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <PageHeader
        title="My Profile"
        subtitle={profile.username}
        breadcrumbs={["Home", "Profile"]}
      />

      <div className="row g-3">
        {/* ═════════ LEFT COLUMN: Identity + Avatar ═════════ */}
        <div className="col-lg-4">
          <Card title="Identity">
            <div
              className="d-flex flex-column align-items-center"
              style={{ padding: "0.5rem 0" }}
            >
              <AvatarUploader
                value={avatar ? resolveImageUrl(avatar) : null}
                onChange={(url) => setAvatar(url)}
                onUpload={handleAvatarUpload}
                size={120}
                fallback={initials || "?"}
              />
              <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {profile.fullName}
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {profile.email}
                </div>
                {profile.role?.displayName && (
                  <div
                    className="text-muted"
                    style={{ fontSize: 11.5, marginTop: 4 }}
                  >
                    {profile.role.displayName}
                  </div>
                )}
              </div>
            </div>
          </Card>

          <div className="mt-3">
            <Card title="Preferences">
              <FormSection compact>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Language, timezone, and date format are configured in Settings
                  by the administrator. Your personal preferences are stored
                  locally per browser.
                </div>
              </FormSection>
            </Card>
          </div>
        </div>

        {/* ═════════ RIGHT COLUMN: Info + Security + Sessions ═════════ */}
        <div className="col-lg-8">
          <Card title="Profile Information" className="mb-3">
            <FormSection description="This is how you appear across the app.">
              <div className="row g-3">
                <div className="col-md-6">
                  <Input
                    label="Full Name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    hint="Shown in lists, audit logs, and receipts"
                  />
                </div>
                <div className="col-md-6">
                  <Input
                    label="Username"
                    value={profile.username}
                    disabled
                    hint="Username can only be changed by an administrator"
                  />
                </div>
                <div className="col-md-6">
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <Input
                    label="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
              </div>

              {saveError && <Alert variant="danger">{saveError}</Alert>}
              {saveOk && <Alert variant="success">Profile saved.</Alert>}

              <div className="d-flex justify-content-end gap-2">
                <Button variant="ghost" onClick={loadProfile} disabled={saving}>
                  Reset
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveProfile}
                  loading={saving}
                >
                  Save Changes
                </Button>
              </div>
            </FormSection>
          </Card>

          <Card title="Change Password" className="mb-3">
            <FormSection description="You'll need your current password to change it.">
              <div className="row g-3">
                <div className="col-md-4">
                  <PasswordField
                    label="Current Password"
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <PasswordField
                    label="New Password"
                    value={newPassword}
                    onChange={setNewPassword}
                    showStrength
                    required
                  />
                </div>
                <div className="col-md-4">
                  <PasswordField
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    required
                  />
                </div>
              </div>

              {pwError && <Alert variant="danger">{pwError}</Alert>}
              {pwOk && <Alert variant="success">Password updated.</Alert>}

              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  onClick={handleChangePassword}
                  loading={pwSaving}
                  disabled={
                    !currentPassword || !newPassword || !confirmPassword
                  }
                >
                  Update Password
                </Button>
              </div>
            </FormSection>
          </Card>

          <Card title="Active Sessions">
            <FormSection
              description="Devices currently signed in to your account."
              actions={
                sessions.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRevokeOthers}
                  >
                    Revoke Others
                  </Button>
                )
              }
            >
              {sessionsLoading ? (
                <div style={{ textAlign: "center", padding: "1rem 0" }}>
                  <Spinner size="sm" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-muted" style={{ fontSize: 12.5 }}>
                  No active sessions found.
                </div>
              ) : (
                <ul className="fh-dash-list" style={{ marginTop: 0 }}>
                  {sessions.map((s) => (
                    <li
                      key={s.id}
                      className="fh-dash-list__item"
                      style={{
                        cursor: "default",
                        opacity: s.isActive ? 1 : 0.55,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div className="fh-dash-list__title">
                          {s.userAgent || "Unknown device"}
                          {s.isCurrent && (
                            <span
                              style={{
                                marginLeft: 8,
                                padding: "1px 6px",
                                borderRadius: 4,
                                fontSize: 10,
                                background: "rgba(13,110,253,0.12)",
                                color: "#0d6efd",
                                fontWeight: 600,
                                verticalAlign: "middle",
                              }}
                            >
                              CURRENT
                            </span>
                          )}
                        </div>
                        <div className="fh-dash-list__sub">
                          {s.ip || "—"} ·{" "}
                          {s.isActive
                            ? `expires ${new Date(s.expiresAt).toLocaleDateString()}`
                            : `revoked ${new Date(s.revokedAt).toLocaleDateString()}`}
                        </div>
                      </div>
                      {s.isActive && !s.isCurrent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeSession(s.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </FormSection>
          </Card>
        </div>
      </div>
    </>
  );
}
